import { usePermissionsStore } from '@/stores/permissions';
import { useAuthStore } from '@/stores/auth';

// Map old permission names to new permission codes
const PERMISSION_MAP = {
  'manage_users': 'users.manage_permissions',
  'view_users': 'users.view',
  'manage_clients': 'clients.edit',
  'view_clients': 'clients.view',
  'view_costs': 'materials.view_costs',
  'manage_pricing': 'materials.manage_pricing',
  'view_profits': 'profit_projections.view',
  'create_purchase_orders': 'purchase_orders.create',
  'approve_purchase_orders': 'purchase_orders.approve',
  'view_purchase_orders': 'purchase_orders.view',
  'manage_sales_orders': 'orders.edit',
  'view_sales_orders': 'orders.view',
  'manage_vendors': 'vendors.edit',
  'view_vendors': 'vendors.view',
  'manage_inventory': 'inventory.adjust',
  'view_inventory': 'inventory.view',
  'manage_recipes': 'recipes.edit',
  'view_recipes': 'recipes.view',
  'manage_batches': 'batches.edit',
  'view_batches': 'batches.view',
  'view_analytics': 'analytics.view',
  'view_projections': 'profit_projections.view'
};

export function setupPermissionGuards(router) {
  router.beforeEach(async (to, from, next) => {
    // Defer store access until guard is executed
    let permissionsStore, authStore;
    try {
      permissionsStore = usePermissionsStore();
      authStore = useAuthStore();
    } catch (error) {
      console.error('Stores not available yet:', error);
      // If stores aren't available, we're probably still initializing
      // Redirect to login as a safe fallback
      if (!to.meta.public) {
        return next({ name: 'Login', query: { redirect: to.fullPath } });
      }
      return next();
    }
    
    // Router guard checking route access for: ${to.path}
    
    // Skip auth check for public routes
    if (to.meta.public) {
      // Public route access allowed
      return next();
    }
    
    // Wait for auth initialization if not done yet
    if (!authStore.initialized) {
      // Waiting for auth initialization
      await authStore.initializeAuth();
      // Auth initialization complete
    }
    
    // Check if user is authenticated
    if (!authStore.isAuthenticated) {
      // User not authenticated, redirecting to login
      return next({ name: 'Login', query: { redirect: to.fullPath } });
    }
    
    // User authenticated, checking permissions
    
    // Initialize permissions if not loaded and not already initializing
    if (!permissionsStore.initialized && (!permissionsStore.myPermissions || Object.keys(permissionsStore.myPermissions).length === 0)) {
      try {
        console.log('📝 Router Guard: Loading user permissions...');
        await permissionsStore.initMyPermissions();
        console.log('✅ Router Guard: Permissions loaded:', Object.keys(permissionsStore.myPermissions).length);
      } catch (error) {
        console.error('❌ Router Guard: Failed to load permissions:', error);
        // Continue anyway - backend will enforce
      }
    } else if (permissionsStore.initialized) {
      // Permissions already initialized, skipping
    }
    
    // Smart routing: Redirect to appropriate dashboard based on permissions
    if (to.path === '/' && permissionsStore.initialized) {
      // Check user's primary focus area
      const hasContentPermissions = permissionsStore.hasPermission('content.view') || 
                                   permissionsStore.hasPermission('content.manage');
      const hasManufacturingPermissions = permissionsStore.hasPermission('materials.view') || 
                                         permissionsStore.hasPermission('recipes.view') ||
                                         permissionsStore.hasPermission('batches.view');
      
      console.log('🎯 Smart routing check:', {
        path: to.path,
        hasContentPermissions,
        hasManufacturingPermissions,
        userRole: authStore.user?.role,
        isContentManager: authStore.user?.role === 'content_manager'
      });
      
      // Content-only users go to content dashboard
      if (hasContentPermissions && !hasManufacturingPermissions) {
        console.log('🔄 Redirecting content manager to content dashboard');
        return next({ path: '/content-dashboard' });
      }
      
      // Also redirect if user role is content_manager
      if (authStore.user?.role === 'content_manager') {
        console.log('🔄 Redirecting content_manager role to content dashboard');
        return next({ path: '/content-dashboard' });
      }
      // Otherwise continue to manufacturing dashboard
    }
    
    // Check route permissions
    if (to.meta.requiresPermission) {
      // Route requires permissions check
      
      const required = Array.isArray(to.meta.requiresPermission) 
        ? to.meta.requiresPermission 
        : [to.meta.requiresPermission];
      
      // Map old permissions to new format
      const mappedPermissions = required.map(perm => PERMISSION_MAP[perm] || perm);
      // Checking mapped permissions
      
      const hasAccess = to.meta.requireAll
        ? permissionsStore.hasAllPermissions(mappedPermissions)
        : permissionsStore.hasAnyPermission(mappedPermissions);
      
      // Access check completed
      
      if (!hasAccess) {
        console.error('Router Guard: Permission check failed');
        
        // Check if it's an admin-only route
        if (to.meta.adminOnly && authStore.user?.role !== 'admin') {
          console.error('Admin-only route, user is not admin');
          return next({ name: 'Forbidden' });
        }
        
        // Regular permission check failed
        console.log('   - Regular permission check failed');
        return next({ name: 'Forbidden' });
      }
    }
    
    // Admin-only routes
    if (to.meta.adminOnly && authStore.user?.role !== 'admin') {
      console.log('❌ Router Guard: Admin-only route, user is not admin');
      return next({ name: 'Forbidden' });
    }
    
    console.log('✅ Router Guard: All checks passed, allowing access');
    next();
  });
}

// Helper to add permission metadata to routes
export function requiresPermission(permission, requireAll = false) {
  return {
    requiresPermission: permission,
    requireAll
  };
}

export function adminOnly() {
  return {
    adminOnly: true
  };
}

// Updated route configurations with new permission system
export const protectedRoutes = [
  {
    path: '/admin/users',
    name: 'UserManagement',
    component: () => import('@/views/admin/UserManagement.vue'),
    meta: {
      ...adminOnly(),
      ...requiresPermission('users.manage_permissions')
    }
  },
  {
    path: '/admin/users/:id/permissions',
    name: 'UserPermissions',
    component: () => import('@/views/admin/UserPermissions.vue'),
    meta: {
      ...adminOnly(),
      ...requiresPermission('users.manage_permissions')
    }
  },
  {
    path: '/materials',
    name: 'Materials',
    component: () => import('@/views/Materials.vue'),
    meta: requiresPermission('materials.view')
  },
  {
    path: '/recipes',
    name: 'Recipes',
    component: () => import('@/views/Recipes.vue'),
    meta: requiresPermission('recipes.view')
  },
  {
    path: '/vendors',
    name: 'Vendors',
    component: () => import('@/views/Vendors.vue'),
    meta: requiresPermission('vendors.view')
  },
  {
    path: '/orders',
    name: 'Orders',
    component: () => import('@/views/SalesOrders.vue'),
    meta: requiresPermission('orders.view')
  },
  {
    path: '/analytics',
    name: 'Analytics',
    component: () => import('@/views/Dashboard.vue'),
    meta: requiresPermission('analytics.view')
  },
  {
    path: '/profit-projections',
    name: 'ProfitProjections',
    component: () => import('@/views/ProfitProjections.vue'),
    meta: requiresPermission('profit_projections.view')
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/Profile.vue'),
    meta: requiresPermission('settings.view')
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('@/views/Forbidden.vue'),
    meta: { public: true }
  }
];